import { useEffect, useId, useState } from 'react';
import { useContactDefaultsQuery, useMeQuery, useSaveContactDefaultsMutation } from './api.js';
import { Alert, Button, Card } from './ui.js';

/**
 * Settings → CRM → Contact defaults: the Contact Type a NEW contact starts with in Review.
 *
 * Only a starting point. Review shows it on every new contact, a person can change it for
 * each one (or for several at once), and nothing is sent until they approve. It is never
 * applied to someone already in Keap. "Ask me every time" — and a workspace that never chose
 * — leaves it blank, so a person picks one before approving, as before this setting existed.
 * Client is the label; Keap stores it as Customer.
 */
export function ContactDefaults() {
  const me = useMeQuery();
  const { data, isLoading, error } = useContactDefaultsQuery();
  const [save, saving] = useSaveContactDefaultsMutation();
  const [value, setValue] = useState<string>('ask');
  const [saved, setSaved] = useState<string | null>(null);
  const id = useId();
  useEffect(() => { if (data) setValue(data.defaultContactType); }, [data?.defaultContactType]);

  if (isLoading) return null;
  if (error || !data) return <Alert tone="error">Contact defaults could not be loaded.</Alert>;
  const canEdit = me.data?.role === 'owner' || me.data?.role === 'admin';
  const types = data.contactTypes;
  // An older server, or a CRM without the field: nothing to choose, so nothing shown.
  if (!types?.supported) return null;
  const changed = value !== data.defaultContactType;

  return (
    <section data-contact-defaults=""><Card className="p-5">
      <h3 className="text-base font-semibold text-slate-900">Contact defaults</h3>
      <label className="mt-3 block min-w-0 text-sm font-medium text-slate-800" htmlFor={`${id}-type`}>Default Contact Type</label>
      <select
        id={`${id}-type`} value={value} disabled={!canEdit || saving.isLoading || !types.available}
        onChange={(e) => { setValue(e.target.value); setSaved(null); }}
        aria-describedby={`${id}-help`}
        className="mt-1 min-h-11 w-full min-w-0 max-w-sm rounded-md border border-slate-300 bg-white px-3 text-base sm:text-sm disabled:bg-slate-50"
      >
        {types.options.map((o) => (
          <option key={o.id} value={o.id}>{o.label}{o.id === data.recommended && !data.configured ? ' (recommended)' : ''}</option>
        ))}
        <option value="ask">Ask me every time</option>
      </select>
      <p id={`${id}-help`} className="mt-1 text-xs text-slate-600">
        Used when adding new contacts to Keap. You can change it for each contact before sending.{' '}
        {value === 'ask'
          ? 'Choose a Contact Type for each new contact.'
          : `New contacts start as ${types.options.find((o) => o.id === value)?.label ?? value} unless you change it.`}
      </p>
      {!types.available ? <p className="mt-1 text-xs text-amber-900">Contact types could not be read from Keap. Try again in a moment.</p> : null}
      {canEdit ? (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Button disabled={!changed || saving.isLoading} onClick={async () => {
            try {
              await save({ defaultContactType: value }).unwrap();
              setSaved('Saved. Contacts already in Review keep the type they show.');
            } catch {
              setSaved('That could not be saved. Try again.');
            }
          }}
          >
            {saving.isLoading ? 'Saving…' : 'Save'}
          </Button>
          <span aria-live="polite" className="text-sm text-slate-700">{saved}</span>
        </div>
      ) : <p className="mt-2 text-xs text-slate-500">Only a workspace owner or admin can change this.</p>}
    </Card></section>
  );
}
